import { FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import classnames from 'classnames';

import { ItemPrice } from './ItemPrice';
import { useItemPriceData } from './hooks/useItemPriceData';

import { PriceDisplayVariant_item$key } from './__generated__/PriceDisplayVariant_item.graphql';
import { PriceDisplayVariant_displayPrice$key } from './__generated__/PriceDisplayVariant_displayPrice.graphql';

import dibsCss from 'dibs-css';
import styles from './PriceDisplayVariant.scss';

const itemFragment = graphql`
    fragment PriceDisplayVariant_item on Item {
        ...ItemPrice_item
        ...useItemPriceData_item
        attributes {
            framingStatus {
                value
            }
        }
    }
`;

const displayPriceFragment = graphql`
    fragment PriceDisplayVariant_displayPrice on ItemDisplayPriceType @relay(plural: true) {
        ...ItemPrice_displayPrice
        ...useItemPriceData_displayPrice
    }
`;

const FRAMED = 'Framed';

export const PriceDisplayVariant: FC<{
    item?: PriceDisplayVariant_item$key | null;
    displayPrice?: PriceDisplayVariant_displayPrice$key | null;
    currency: string;
}> = ({ item: itemRef, displayPrice: displayPriceRef, currency }) => {
    const item = useFragment(itemFragment, itemRef);
    const displayPrice = useFragment(displayPriceFragment, displayPriceRef);
    const priceData = useItemPriceData({
        item,
        displayPrice,
        currency,
    });

    if (!priceData) {
        return null;
    }

    const { holdType, holdExpiration, privateOfferExpiration } = priceData;
    const isFramed = item?.attributes?.framingStatus?.[0]?.value === FRAMED;

    const showHoldExpiration = holdExpiration && holdType === 'HOLD_CUSTOM';
    const showPrivateOfferExpiration = privateOfferExpiration && !holdExpiration;

    return (
        <div data-tn="price-wrapper">
            {holdType && (
                <div className={classnames(dibsCss.sassyFontBodyLargeHeavy, dibsCss.textNoir)}>
                    {holdType === 'HOLD' && (
                        <FormattedMessage
                            id="PriceDisplayVariant.holdType"
                            defaultMessage="On Hold"
                        />
                    )}
                    {holdType === 'HOLD_CUSTOM' && (
                        <FormattedMessage
                            id="PriceDisplayVariant.holdTypeCustom"
                            defaultMessage="On Hold For You"
                        />
                    )}
                </div>
            )}

            {(showHoldExpiration || showPrivateOfferExpiration) && (
                <div
                    className={classnames({
                        [dibsCss.sassyFontBodySmall]: showHoldExpiration,
                        [dibsCss.textAlertsecondary]: showHoldExpiration,
                        [dibsCss.sassyFontBodySmallHeavy]: showPrivateOfferExpiration,
                        [dibsCss.textNoir]: showPrivateOfferExpiration,
                    })}
                >
                    <FormattedMessage
                        id="PriceDisplayVariant.expiration"
                        defaultMessage="Expires {time}"
                        values={{
                            time: showHoldExpiration ? holdExpiration : privateOfferExpiration,
                        }}
                    />
                </div>
            )}

            {(holdType || showHoldExpiration || showPrivateOfferExpiration) && (
                <div className={styles.divider} />
            )}

            <ItemPrice
                item={item}
                displayPrice={displayPrice}
                currency={currency}
                priceNote={
                    isFramed ? (
                        <FormattedMessage
                            id="PriceDisplayVariant.frameIncluded"
                            defaultMessage="Frame Included"
                        />
                    ) : null
                }
            />
        </div>
    );
};
